// Sentry
import * as Sentry from '@sentry/react';
import debug from 'debug';

// GQL
import type { DocumentNode } from '@apollo/client';
import { client } from '../client';
import { coordsFromPk, type MeshPoint } from '../../utils/coordsFromPk';
import { locationMapGeometry } from '../../utils/locationMapGeometry';
import { getCachedRoadMesh } from '../../offline/roadMeshStore';
import { pkEquals } from '../../utils/pkCompare';

// Configuración de logs con namespace para actions
const log = debug('rois:gql:selectors:actions');
const logActions = debug('rois:gql:selectors:actions:list');
const logSelector = debug('rois:gql:selectors:actions:operators-vehicles');
const logSelectorData = debug('rois:gql:selectors:actions:data');
const logSelectorUser = debug('rois:gql:selectors:actions:user');
const logSelectorVehicle = debug('rois:gql:selectors:actions:vehicle');

if (import.meta.env.DEV) {
  debug.enable('rois:gql:selectors:actions*');
}
import i18n from '../../i18n';
import {
  GET_CONCESSIONS,
  GET_ORDER_BY_ID,
  GET_SELECTORS_DATA,
} from '../queries/orders/orders';
import {
  FETCH_USERS,
  GET_ACTIVE_OPERATORS,
  GET_OPERATORS,
  GET_TEAM_BY_ID, // Still used for Action/Order/Accident entities (not User)
  FETCH_TEAMS,
} from '../queries/users';
import {
  FETCH_VEHICLES,
  GET_ACTIVE_VEHICLES,
  GET_VEHICLE_BY_ID,
  GET_VEHICLE_OCCUPANT_BY_ID,
} from '../queries/vehicles';
import {
  FETCH_ACTIVITYS,
  GET_ACTIVITY_BY_ID,
} from '../queries/actions/activitys';
import { FETCH_ASSETS, GET_ASSET_BY_ID } from '../queries/actions/assets';
import { GET_ACTION_BY_ID } from '../queries/actions/actions';
import {
  GET_CONCESSION_BY_ID,
  GET_ROADS_BY_IDS,
  GET_ROAD_BY_ID,
} from '../queries/geoPositions/roads';
import { GET_ACTION_BUDGET_ITEM } from '../queries/budget/actionBudgetItems';
import { GET_BUDGET_ITEM } from '../queries/budget/budgetItems';
import { GET_BUDGET_CATEGORY } from '../queries/budget/budgetCategories';
import {
  GET_MATERIAL_BY_ID,
  GET_MATERIAL_UNIT_BY_ID,
} from '../queries/actions/materials';
import {
  GET_AUX_MACHINE_BY_ID,
  GET_AUX_MACHINE_UNIT_BY_ID,
} from '../queries/actions/auxMachines';
import {
  GET_ANIMAL_BY_ID,
  GET_ANIMAL_STATUS_BY_ID,
  GET_ANIMAL_UNIT_BY_ID,
} from '../queries/actions/animals';
import {
  FETCH_CAPITOLS,
  FETCH_PREDEFINED_ORDERS,
  GET_CAPITOL_BY_ID,
  GET_ELEMENT_ORDERS_BY_ID,
  GET_ELEMENT_ORDER_BY_ID,
  GET_SUB_CAPITOLS_BY_IDS,
  GET_SUB_CAPITOL_BY_ID,
} from '../queries/orders/predefinedData';
import { groupLanes, groupTracks } from '../../data/orders';
import { laneLabel, trackLabel } from '../../utils/catalogLabels';
import { formatDirectionCellLabel } from '../../utils/surveillanceRoute';
import { ensureLabelNamespaces } from '../../utils/i18nReady';
import { isBasicAction } from '../../utils/order';
import { GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID } from '../queries/surveillanceRoute';
import { GET_IMAGE_BY_ID } from '../queries/accidents/accidents';
import { GET_POSITION_ROADS } from '../queries/geoPositions/geoPositionRoads';
import { GET_POSITION_ACTIONS } from '../queries/geoPositions/geoPositionActions';
import { GET_USER_BY_ID } from '../queries/users';
import { formatUserFullName } from '../../utils/registeredByOperators';
import { GET_EXTERNAL_WORK_BY_ID } from '../queries/actions/externalWorks';
import { GET_EXTERNAL_COMPANY_BY_ID } from '../queries/actions/externalCompanies';
import {
  resolveIsLaneCut,
  needsLaneCutFallback,
  EMPTY_LANE_CUT_IDS,
} from '../../utils/laneCut';
import { loadLaneCutIds } from './laneCutCatalog';
import {
  needsMaterialsCatalog,
  buildMaterialNameMap,
  resolveMaterialNames,
} from '../../utils/actionMaterials';
import { fetchMaterialsCatalog } from '../services/materialsCatalog';
import { warmupOrdersByIds } from './warmupOrders';
import { warmupExternalWorks } from './warmupExternalWorks';
import { warmupVehicleOccupantsByIds } from './warmupVehicleOccupants';

// Utils
import { pkFormatter } from '../../utils/transform';
import { roleCanViewBudgets } from '../../utils/rolePermissions';
import { decompressSessionStorage } from '../../utils/sessionStorage';

// Types
import { ImageData } from '../../components/inputs/InputLoadImages/types';
import { MaterialsType } from '../../components/display/tables/summaryTables/MaterialsTable/types';
import { MachineryTypes } from '../../components/forms/ActionForms/MachineryForm/types';

/**
 * Lee el rol activo del usuario desde sessionStorage. Mismo patrón que
 * `WorkOrderDetailed/loader.ts` y `CampaignDetailed/loader.ts`. Si falla
 * (sesión rota, ambiente test, etc.) devuelve null.
 *
 * Necesario porque los selectores corren fuera de React (loaders) y los
 * roles ciegos a budgets (capataz) NO pueden lanzar queries de partidas:
 * el backend respondería Forbidden y rompería el resumen/prefetch.
 */
const getCurrentRole = (): string | null => {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(decompressSessionStorage(raw));
    return parsed?.user?.rolActive ?? null;
  } catch {
    return null;
  }
};

export const selectActiveOperatorsAndVehicles = async (
  onlyActives: boolean,
  /**
   * De dónde salen los datos. Por defecto `network-only`, que es como lo han
   * pedido siempre todos los llamadores: esto no cambia su comportamiento.
   *
   * 🔴 `cache-only` existe para el camino de respaldo con cobertura degradada
   * (spec 2026-09-11): ahí una consulta `network-only` ni responde ni falla, y
   * un `Promise.all` con una sola de ellas dentro deja colgado al asistente
   * entero. Leer de caché no puede colgarse.
   */
  options: { fetchPolicy?: 'network-only' | 'cache-only' } = {}
) => {
  const { fetchPolicy = 'network-only' } = options;
  try {
    const [
      {
        data: { concessions },
      },
      {
        data: { vehicles: vehiclesUnsorted },
      },
      ,
      {
        data: { teams },
      },
    ] = await Promise.all([
      client.query({
        query: GET_CONCESSIONS,
        fetchPolicy, // ⚠️ Por defecto siempre datos frescos
      }),
      client.query({
        query: FETCH_VEHICLES,
        fetchPolicy, // ⚠️ Por defecto frescos (incluye vehículos recién creados)
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy, // ⚠️ Por defecto siempre datos frescos
      }),
      client.query({
        query: FETCH_TEAMS,
        fetchPolicy, // ⚠️ Equipos para filtrado
      }),
    ]);

    // Create lookup maps: userId -> team, vehicleId -> teams array (M2M)
    const userTeamMap = new Map<string, { id: string; name: string }>();
    const vehicleTeamMap = new Map<string, Array<{ id: string; name: string }>>();

    teams.forEach((team: { id: string; name: string; userIDs: string[]; vehicleIDs: string[] }) => {
      team.userIDs?.forEach((userId: string) => {
        userTeamMap.set(userId, { id: team.id, name: team.name });
      });
      // ✅ M2M: Accumulate teams in array instead of overwriting
      team.vehicleIDs?.forEach((vehicleId: string) => {
        const existingTeams = vehicleTeamMap.get(vehicleId) || [];
        vehicleTeamMap.set(vehicleId, [...existingTeams, { id: team.id, name: team.name }]);
      });
    });

    let operators;
    let vehiclesData = vehiclesUnsorted;

    if (onlyActives) {
      const { readActiveOperators } = client.readQuery({
        query: GET_ACTIVE_OPERATORS,
      });

      const { readActiveVehicles } = client.readQuery({
        query: GET_ACTIVE_VEHICLES,
      });

      // ✅ Filtrar vehículos de CONSTRUCTION para operarios y vehículos
      vehiclesData = readActiveVehicles.filter(
        (vehicle: any) => vehicle.usage === 'CONSTRUCTION'
      );

      operators = readActiveOperators;
    } else {
      const { readOperators } = client.readQuery({
        query: GET_OPERATORS,
      });
      operators = readOperators;
    }
    const operatorsWithConcession = await Promise.all(
      operators.map(
        async (operator: {
          concessionIDs: string[];
          email: string;
          id: string;
        }) => {
          let concessionInnerName;
          // NOTE: teamIDs removed from user model - always use concessionIDs directly
          // if (operator.teamIDs?.[0]) {
          //   const {
          //     data: { team },
          //   } = await client.query({
          //     query: GET_TEAM_BY_ID,
          //     variables: {
          //       id: operator.teamIDs[0],
          //     },
          //   });
          //
          //   concessionInnerName = [
          //     concessions.find(
          //       (concession: { id: string }) =>
          //         team.concessionIDs[0] === concession.id
          //     ).name,
          //   ];
          // } else {
          if (operator.concessionIDs && operator.concessionIDs.length > 1) {
            concessionInnerName = concessions.map(
              (concessionInner: { name: string }) => concessionInner.name
            );
          } else if (operator.concessionIDs && operator.concessionIDs.length === 1) {
            const {
              data: { concession },
            } = await client.query({
              query: GET_CONCESSION_BY_ID,
              variables: {
                id: operator.concessionIDs[0],
              },
            });
            concessionInnerName = [concession.name];
          } else {
            concessionInnerName = [];
          }
          // }

          const operatorTeam = userTeamMap.get(operator.id);
          return {
            concession: concessionInnerName,
            team: operatorTeam?.name || '',
            teamId: operatorTeam?.id || '',
            ...operator,
          };
        }
      )
    );
    operatorsWithConcession.sort((a, b) => a.name.localeCompare(b.name));
    let vehicles = [...vehiclesData].sort((a, b) =>
      a.plate.localeCompare(b.plate)
    );
    vehicles = await Promise.all(
      vehicles.map(async (vehicle: { concessionIDs: string[]; id: string; plate?: string }) => {
        let concessionName;

        if (vehicle?.concessionIDs?.length) {
          const {
            data: { concession },
          } = await client.query({
            query: GET_CONCESSION_BY_ID,
            variables: {
              id: vehicle.concessionIDs[0],
            },
          });

          concessionName = concession.name;
        }

        const vehicleTeams = vehicleTeamMap.get(vehicle.id) || [];
        return {
          ...vehicle,
          concession: concessionName,
          // ✅ M2M: Return arrays for multiple teams
          teams: vehicleTeams.map((t) => t.name),
          teamIDs: vehicleTeams.map((t) => t.id),
        };
      })
    );

    return {
      vehicles,
      operators: operatorsWithConcession,
      teams: teams.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })),
    };
  } catch (err) {
    Sentry.captureException(err);
    log(i18n.t('errors:gettingUserData'), err);
    // ✅ FIX: Return safe default object to prevent component crash
    return { vehicles: [], operators: [], teams: [] };
  }
};

export const selectAssetsAndActivitys = async () => {
  try {
    const [
      {
        data: { assets },
      },
      {
        data: { activitys },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_ASSETS,
      }),
      client.query({
        query: FETCH_ACTIVITYS,
      }),
    ]);
    return {
      activitys,
      assets,
    };
  } catch (err) {
    Sentry.captureException(err);
    log(i18n.t('errors:gettingAssetsAndActivities'), err);
    return { activitys: [], assets: [] };
  }
};

export const selectActivitys = async () => {
  try {
    const {
      data: { activitys },
    } = await client.query({
      query: FETCH_ACTIVITYS,
    });

    return activitys;
  } catch (err) {
    Sentry.captureException(err);
    log('Error getting the activitys', err);
  }
};

export const selectOrderWithActionBasicInfoById = async (
  orderId: string,
  actionId: string
) => {
  try {
    const [
      {
        data: { order },
      },
      {
        data: { action },
      },
    ] = await Promise.all([
      client.query({
        query: GET_ORDER_BY_ID,
        variables: { id: orderId },
      }),
      client.query({
        query: GET_ACTION_BY_ID,
        variables: { id: actionId },
      }),
    ]);

    const {
      data: { order: parentOrder },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });

    const isBasic = isBasicAction({
      containerType: order.containerType,
      parentOrderType: parentOrder.type,
    });

    // Contexto del recorrido para la cabecera (bloque D). Solo se paga en una
    // básica, y solo si hace falta: la actuación normal no lanza nada nuevo.
    //
    // Dos traducciones de id, ambas del patrón entidad doble:
    //   · Order gemela del RV  → id de ENTIDAD `SurveillanceRoute`, que es lo
    //     que pide la ruta `/surveillance-route/:routeId` (confundirlos = 404
    //     mudo). La query de tarjeta es la variante ligera y comparte caché.
    //   · Order del tramo      → su ROID, para pintar `Tr.NN`.
    // Fallo en cualquiera → se pinta sin enlace; nunca rompe la validación.
    const linkedSegmentOrderId =
      order.basicActionLinkedSegmentOrderIDs?.[0] ?? null;
    let routeId: string | null = null;
    let linkedSegmentRoid: string | null = null;

    if (isBasic) {
      const [routeResult, segmentResult] = await Promise.allSettled([
        client.query({
          query: GET_SURVEILLANCE_ROUTE_CARD_BY_ORDER_ID,
          variables: { orderId: parentOrder.id },
          fetchPolicy: 'cache-first',
        }),
        linkedSegmentOrderId
          ? client.query({
              query: GET_ORDER_BY_ID,
              variables: { id: linkedSegmentOrderId },
              fetchPolicy: 'cache-first',
            })
          : Promise.resolve(null),
      ]);

      if (routeResult.status === 'fulfilled') {
        routeId = routeResult.value?.data?.surveillanceRouteByOrderId?.id ?? null;
      }
      if (segmentResult.status === 'fulfilled' && segmentResult.value) {
        linkedSegmentRoid = segmentResult.value.data?.order?.roid ?? null;
      }
    }

    return {
      pkInit: pkFormatter(action.pkInitKm, action.pkInitMeter),
      pkEnd: pkFormatter(action.pkEndKm, action.pkEndMeter),
      // Bug descripciones (2026-06-05): la "Descripción de la actuación" de la
      // cabecera = validada || operario (regla única, misma que el resto de vistas).
      // Antes era solo descriptionValidated → mismo label mostraba dato distinto que la OT genérica.
      actionDescription: action.descriptionValidated || order.description,
      orderDescription: parentOrder.description,
      state: order.state,
      indexExpedition: order.indexExpedition,
      registerEndDate: order.registerEndDate,
      orderId,
      parentOrderState: parentOrder.state,
      // Actuació bàsica: el traje reducido depende del DATO, no de la pantalla.
      // `containerType` es el camino principal (denormalizado, ya en el
      // fragment NEW_ORDER); el tipo del padre es el respaldo para documentos
      // anteriores al backfill — y no cuesta nada, porque esta Order padre ya
      // se estaba cargando para `orderDescription`/`parentOrderState`.
      isBasicAction: isBasic,
      parentOrderIdFromData: parentOrder.id,
      parentRouteRoid: parentOrder.roid,
      linkedSegmentOrderId,
      // De qué recorrido viene y qué tramo resolvió — sin esto la básica se
      // abre huérfana desde la tabla de validación y nada dice que salió de
      // una patrulla.
      routeId,
      linkedSegmentRoid,
    };
  } catch (err) {
    Sentry.captureException(err);
    log(err);
  }
};

/**
 * Consulta un elemento de catálogo por id SOLO si hay id; si no, devuelve el
 * campo a `null` sin tocar la red.
 *
 * 🔴 Una actuación BÁSICA del recorrido nace sin capítulo, subcapítulo ni
 * actividad: se crea en el clic de «Crear Actuación» y eso se rellena en el
 * último paso del asistente. Las queries `capitol(id: ID!)` y compañía
 * rechazan un id vacío con un 400, y como iban en un `Promise.all` tumbaban la
 * carga entera: el resumen decía «no existe o no tienes permisos» y la
 * parcial no se podía ni anular ni terminar, bloqueando validar el recorrido
 * (diagnóstico 2026-10-08 § 4). Es el mismo arreglo que ya tenía
 * `elementOrder` (C-11.10), generalizado.
 */
const queryOptionalById = <TField extends string>(
  query: DocumentNode,
  id: string | null | undefined,
  field: TField
) =>
  id
    ? client.query({ query, variables: { id } })
    : Promise.resolve({ data: { [field]: null } as Record<TField, null> });

/**
 * @param opts.preferCache — SOLO para los loaders del detalle de OT/campaña
 * (`prefetchChildrenSummaries`): `selectWorkOrderWithChildren` acaba de bajar
 * por red (mismo documento GET_ACTION_BY_ID / GET_ORDER_BY_ID, mismas
 * variables) la Action y su Order de CADA hija — repetirlos network-only
 * milisegundos después duplica el N+1 (getActionById×30 + GetOrderById×30
 * medidos en prod). Con `preferCache: true` esos dos fetches van cache-first
 * y dan hit garantizado (selection-set idéntico, datos frescos del mismo
 * load). La navegación DIRECTA al resumen (ActionSummary/loader) NO pasa el
 * flag y conserva su network-only de siempre (frescura post-edición).
 */
export const selectActionById = async (
  actionId: string,
  opts?: { preferCache?: boolean }
) => {
  const logSelector = debug('rois:selector:action:byId');
  if (import.meta.env.DEV) {
    debug.enable('rois:selector:action:*');
  }

  logSelector(`🔍 [START] selectActionById para actionId: ${actionId}`);

  const freshOrCache = opts?.preferCache ? 'cache-first' : 'network-only';

  try {
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: {
        id: actionId,
      },
      fetchPolicy: freshOrCache,
    });

    logSelector(
      `📋 Action obtenida: ${action.id}, orderIDs: ${action.orderIDs}`
    );

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: action.orderIDs[0],
      },
      fetchPolicy: freshOrCache,
    });

    logSelector(
      `📦 Order obtenida: ${order.id}, imageIDs count: ${order.imageIDs?.length || 0}`
    );

    const [
      {
        data: { concession },
      },
      {
        data: { concessions },
      },
      {
        data: { capitol },
      },
      {
        data: { subCapitol },
      },
      {
        data: { road },
      },
      {
        data: { elementOrder },
      },
    ] = await Promise.all([
      queryOptionalById(
        GET_CONCESSION_BY_ID,
        action.concessionIDs?.[0],
        'concession'
      ),

      client.query({
        query: GET_CONCESSIONS,
      }),

      // Capítulo y subcapítulo pueden faltar en una básica del recorrido
      // a medias: ver `queryOptionalById`.
      queryOptionalById(GET_CAPITOL_BY_ID, order.capitolIDs?.[0], 'capitol'),
      queryOptionalById(
        GET_SUB_CAPITOL_BY_ID,
        order.subCapitolIDs?.[0],
        'subCapitol'
      ),
      queryOptionalById(GET_ROAD_BY_ID, action.roadIDs?.[0], 'road'),
      // C-11.10: elementOrder es opcional. Si la action no tiene
      // `elementOrderIDs` poblados (caso normal — el campo no se rellena en
      // el wizard NewAction), no disparar la query — `id: undefined` causa
      // `Variable "$id" of required type "ID!" was not provided` y rompe el
      // loader del summary. Mismo guard que `selectActionForEdition`
      // (línea 1929 de este fichero).
      order.elementOrderIDs?.length > 0
        ? client.query({
            query: GET_ELEMENT_ORDER_BY_ID,
            variables: {
              id: order.elementOrderIDs[0],
            },
          })
        : Promise.resolve({ data: { elementOrder: undefined } }),
    ]);

    let geoPositionData;

    // Sin carretera no hay geometría que pedir (y `roadId` vacío es un 400).
    if (action.direction && action.roadIDs?.[0]) {
      const {
        data: { geoPositionRoads },
      } = await client.query({
        query: GET_POSITION_ROADS,
        variables: {
          input: {
            pkInitKm: action.pkInitKm,
            pkInitMeter: action.pkInitMeter,
            pkEndKm: action.pkEndKm,
            pkEndMeter: action.pkEndMeter,
            roadId: action.roadIDs[0],
            direction: action.direction,
          },
        },
      });

      geoPositionData = geoPositionRoads;
    }

    // La actuación está en un PUNTO, no en un trecho: se pinta un marcador y no
    // una línea. Lo decide `locationMapGeometry`, la misma función que usa el
    // asistente — si no, la misma actuación se vería de dos formas.
    const isPunctual =
      action.pkInitKm != null &&
      action.pkInitMeter != null &&
      action.pkEndKm != null &&
      action.pkEndMeter != null &&
      pkEquals(
        { km: action.pkInitKm, meter: action.pkInitMeter },
        { km: action.pkEndKm, meter: action.pkEndMeter }
      );

    const geometry = locationMapGeometry({
      isPunctual,
      meshPoint: isPunctual
        ? coordsFromPk(
            { km: action.pkInitKm, meter: action.pkInitMeter },
            getCachedRoadMesh(action.roadIDs[0])
          )
        : null,
      vertices: (geoPositionData ?? []).map(
        (position: { latitude: number; longitude: number }) => ({
          lat: position.latitude,
          lng: position.longitude,
        })
      ),
    });

    // `null` es alcanzable: más abajo el centro cae a la posición GPS guardada
    // cuando la geometría no ha dado ninguno.
    let centerMap: MeshPoint | null | undefined = geometry.center ?? undefined;
    const path = geometry.path.length > 0 ? geometry.path : undefined;
    const pointMap = geometry.point;

    // Optional Fields
    let animals, activityValue, assetValue, machineryValue, materialsValue;
    let geolocationValues;
    const images: ImageData[] = [];
    let sketch: ImageData | null = null;

    if (action.geoPositionActionIDs.length > 0) {
      const {
        data: { geoPositionAction },
      } = await client.query({
        query: GET_POSITION_ACTIONS,
        variables: {
          id: action.geoPositionActionIDs[0],
        },
      });

      geolocationValues = geoPositionAction;
      if (!centerMap || (!centerMap?.lat && !centerMap?.lng)) {
        centerMap =
          geolocationValues &&
            geolocationValues.latitude &&
            geolocationValues.longitude
            ? {
              lat: geolocationValues.latitude,
              lng: geolocationValues.longitude,
            }
            : null;
      }
    }

    if (order.imageIDs.length > 0) {
      logSelector(`🖼️  Procesando ${order.imageIDs.length} imágenes...`);

      // C-11.13: recolectar candidatos de sketch en una lista en lugar de
      // asignar `sketch = image` dentro del map. La lógica anterior tenía
      // race en `Promise.all`: cuál sketch "ganaba" dependía de qué query
      // resolvía último (no determinista). Si por cualquier razón hay
      // múltiples records `_canvas` (regresión, sync offline, edits previos
      // antes del fix de saveData) este selector ahora siempre escoge el
      // más reciente por `creatingDate`. Defense-in-depth.
      const sketchCandidates: any[] = [];

      await Promise.all(
        order.imageIDs.map(async (imageId: string) => {
          const {
            data: { image },
          } = await client.query({
            query: GET_IMAGE_BY_ID,
            variables: {
              id: imageId,
            },
          });

          if (!image) return; // record borrado pero id huérfano en imageIDs[]

          logSelector(`  📸 Imagen: ${image.name} (ID: ${image.id})`);

          if (image.name.includes('_canvas')) {
            sketchCandidates.push(image);
            logSelector(`  ✅ SKETCH DETECTADO: ${image.name}`);
          } else {
            images.push(image);
            logSelector(`  📷 Imagen normal añadida: ${image.name}`);
          }
        })
      );

      if (sketchCandidates.length > 0) {
        sketchCandidates.sort(
          (a, b) =>
            new Date(b.creatingDate).getTime() -
            new Date(a.creatingDate).getTime(),
        );
        sketch = sketchCandidates[0];
        if (sketchCandidates.length > 1) {
          logSelector(
            `  ⚠️ ${sketchCandidates.length} sketches encontrados, usando el más reciente: ${sketchCandidates[0].name}`,
          );
        }
      }

      logSelector(
        `📊 Resumen imágenes: ${images.length} normal(es), ${sketch ? '1 sketch' : 'sin sketch'}`
      );
    } else {
      logSelector(`⚠️  No hay imágenes en la orden`);
    }

    if (action.animalUnitIDs.length > 0) {
      animals = await Promise.all(
        action.animalUnitIDs.map(async (animalUnitId: string) => {
          const {
            data: { animalUnit },
          } = await client.query({
            query: GET_ANIMAL_UNIT_BY_ID,
            variables: {
              id: animalUnitId,
            },
          });

          const {
            data: { animal },
          } = await client.query({
            query: GET_ANIMAL_BY_ID,
            variables: {
              id: animalUnit.animalIDs[0],
            },
          });

          let status = '-';
          if (animalUnit.animalStatusIDs.length > 0) {
            const {
              data: { animalStatus },
            } = await client.query({
              query: GET_ANIMAL_STATUS_BY_ID,
              variables: {
                id: animalUnit.animalStatusIDs[0],
              },
            });

            status = animalStatus.description;
          }

          return {
            codiAn: animal.animalId,
            animal: animal.name,
            ut: animalUnit.unit,
            status,
          };
        })
      );
    }

    if (action.activityIDs.length > 0) {
      const {
        data: { activity },
      } = await client.query({
        query: GET_ACTIVITY_BY_ID,
        variables: {
          id: action.activityIDs[0],
        },
      });

      activityValue = activity;
    }

    if (action.assetIDs.length > 0) {
      const {
        data: { asset },
      } = await client.query({
        query: GET_ASSET_BY_ID,
        variables: {
          id: action.assetIDs[0],
        },
      });

      assetValue = asset;
    }

    let works;
    let externalOperatorsCount = 0;
    if (action.externalWorksIDs.length > 0) {
      // Warm-up (receta #93): DOS queries batch (works + companies) en vez de
      // 2×W requests fila a fila. `network-only` porque este selector no tiene
      // reFetch/polling — la frescura de cada carga la garantiza el batch (los
      // ids no cambian al editar operatorsNumber, cache-first repetiría caché).
      // Si falla (offline/backend sin batch), los per-row cache-first caen a
      // red solos vía los redirects `Query.externalWork/externalCompany`.
      await warmupExternalWorks([action.externalWorksIDs], false, 'network-only');
      works = await Promise.all(
        action.externalWorksIDs.map(async (externalWorkId: string) => {
          const {
            data: { externalWork },
          } = await client.query({
            query: GET_EXTERNAL_WORK_BY_ID,
            variables: {
              id: externalWorkId,
            },
            // Hit garantizado tras el warm-up (mismos campos, ver #93).
            fetchPolicy: 'cache-first',
          });

          const {
            data: { externalCompany },
          } = await client.query({
            query: GET_EXTERNAL_COMPANY_BY_ID,
            variables: {
              id: externalWork.externalCompanyIDs[0],
            },
            // Hit garantizado tras el warm-up (calienta externalCompanyIDs[0]).
            fetchPolicy: 'cache-first',
          });

          externalOperatorsCount += externalWork.operatorsNumber;

          return {
            company: externalCompany.companyName,
            operatorsNumber: externalWork.operatorsNumber,
          };
        })
      );
    }

    if (action.auxMachineUnitIDs.length > 0) {
      machineryValue = await Promise.all(
        action.auxMachineUnitIDs.map(async (auxMachineId: string) => {
          const {
            data: { auxMachineUnit },
          } = await client.query({
            query: GET_AUX_MACHINE_UNIT_BY_ID,
            variables: {
              id: auxMachineId,
            },
          });

          const {
            data: { auxMachine },
          } = await client.query({
            query: GET_AUX_MACHINE_BY_ID,
            variables: {
              id: auxMachineUnit.auxMachineIDs[0],
            },
          });

          return {
            codiMaq: auxMachine.auxMachineId,
            maquina: auxMachine.description,
            um: auxMachine.typeOfMeasurement,
            ut: auxMachineUnit.unit,
          };
        })
      );
    }

    if (action.materialUnitIDs.length > 0) {
      materialsValue = await Promise.all(
        action.materialUnitIDs.map(async (materialUnitId: string) => {
          const {
            data: { materialUnit },
          } = await client.query({
            query: GET_MATERIAL_UNIT_BY_ID,
            variables: {
              id: materialUnitId,
            },
          });

          const {
            data: { material },
          } = await client.query({
            query: GET_MATERIAL_BY_ID,
            variables: {
              id: materialUnit.materialIDs[0],
            },
          });

          return {
            codiMat: material.materialId,
            material: material.name,
            ut: materialUnit.unit,
            um: material.typeOfMeasurement,
          };
        })
      );
    }

    // ✅ CARGAR BUDGET ITEMS PARA EL RESUMEN
    let budgetItemsValue: any[] = [];

    // 🔍 DEBUG: Log de actionBudgetItemIDs
    logSelector(
      '🔍 action.actionBudgetItemIDs:',
      action.actionBudgetItemIDs
    );
    logSelector('🔍 action completo:', action);

    // Roles ciegos a budgets (capataz) NO lanzan las queries de partidas:
    // el backend respondería Forbidden. Devuelven lista vacía.
    const canViewBudgets = roleCanViewBudgets(getCurrentRole());

    if (
      canViewBudgets &&
      action.actionBudgetItemIDs &&
      action.actionBudgetItemIDs.length > 0
    ) {
      logSelector(
        '🔍 Procesando budget items:',
        action.actionBudgetItemIDs
      );

      budgetItemsValue = await Promise.all(
        action.actionBudgetItemIDs.map(
          async (actionBudgetItemId: string, index: number) => {
            logSelector(
              `🔍 Procesando item ${index + 1}/${action.actionBudgetItemIDs.length}:`,
              actionBudgetItemId
            );

            try {
              // 1. Obtener ActionBudgetItem
              logSelector(
                '📋 Query GET_ACTION_BUDGET_ITEM para ID:',
                actionBudgetItemId
              );
              const {
                data: { actionBudgetItem },
              } = await client.query({
                query: GET_ACTION_BUDGET_ITEM,
                variables: {
                  id: actionBudgetItemId,
                },
              });

              // 2. Obtener BudgetItem
              logSelector(
                '📋 Query GET_BUDGET_ITEM para ID:',
                actionBudgetItem.budgetItemId
              );
              const {
                data: { budgetItem },
              } = await client.query({
                query: GET_BUDGET_ITEM,
                variables: {
                  id: actionBudgetItem.budgetItemId,
                },
              });

              // 3. Obtener BudgetCategory
              logSelector(
                '📋 Query GET_BUDGET_CATEGORY para ID:',
                budgetItem.budgetCategoryId
              );
              const {
                data: { budgetCategory },
              } = await client.query({
                query: GET_BUDGET_CATEGORY,
                variables: {
                  id: budgetItem.budgetCategoryId,
                },
              });

              const result = {
                codiPartida: budgetItem.code,
                partida: budgetItem.description,
                quantity: actionBudgetItem.quantity,
                categoria: budgetCategory.name,
                categoryColor: budgetCategory.color,
                unitPrice: budgetItem.unitPrice,
                totalAmount: actionBudgetItem.totalAmount,
              };

              logSelector(
                '🎯 Item procesado exitosamente:',
                result
              );
              return result;
            } catch (error) {
              console.error(
                `❌ Error procesando item ${actionBudgetItemId}:`,
                error
              );
              throw error;
            }
          }
        )
      );
    } else {
      logSelector(
        'ℹ️ No hay actionBudgetItemIDs para procesar'
      );
    }

    // 🔍 DEBUG: Log de budgetItemsValue final
    logSelector(
      '🔍 budgetItemsValue final:',
      budgetItemsValue
    );

    logSelector(`👥 Procesando operarios: ${action.userIDs.length}`);
    const operators = await Promise.all(
      action.userIDs.map(async (userID: string, index: number) => {
        logSelector(
          `👤 Procesando operario ${index + 1}/${action.userIDs.length}: ${userID}`
        );
        const {
          data: { user },
        } = await client.query({
          query: GET_USER_BY_ID,
          variables: {
            input: {
              id: userID,
            },
          },
          // Display de nombres (nom/cognom/concession) — dato maestro que baja
          // FetchUsers en boot/loader; selection-set ⊂ FETCH_USERS verificado
          // → hit vía redirect Query.user. Sin uso transaccional aquí.
          fetchPolicy: 'cache-first',
        });

        logSelector(`👤 Usuario obtenido: ${user.name} ${user.surName}`);

        let concessionInnerName;
        // NOTE: teamIDs removed from user model - always use concessionIDs directly
        // if (user.teamIDs?.[0]) {
        //   const {
        //     data: { team },
        //   } = await client.query({
        //     query: GET_TEAM_BY_ID,
        //     variables: {
        //       id: user.teamIDs[0],
        //     },
        //     fetchPolicy: 'network-only',
        //   });
        //
        //   concessionInnerName = concessions.find(
        //     (concession: { id: string }) =>
        //       team.concessionIDs[0] === concession.id
        //   ).name;
        // } else {
        if (user.concessionIDs && user.concessionIDs.length > 1) {
          concessionInnerName = concessions.map(
            (concessionInner: { name: string }) => concessionInner.name
          );
        } else {
          const {
            data: { concession },
          } = await client.query({
            query: GET_CONCESSION_BY_ID,
            variables: {
              id: user.concessionIDs[0],
            },
            // Display de nombre de concesión — GET_CONCESSIONS (mismos campos)
            // se fetchea unas líneas arriba en este mismo selector → hit vía
            // redirect Query.concession. Catálogo maestro, no transaccional.
            fetchPolicy: 'cache-first',
          });
          concessionInnerName = concession.name;
        }
        // }

        return {
          nom: user.name,
          cognom: user.surName,
          concession: concessionInnerName,
        };
      })
    );

    // ========== CARGAR VEHÍCULOS DE TRANSPORTE (con conductor) ==========
    // Warm-up (receta #93 aplicada a ocupantes): UNA query batch en vez de
    // O requests fila a fila. Con `preferCache` (flujo detalle de OT/campaña)
    // se salta: `selectWorkOrderWithChildren` ya calentó los ocupantes de
    // TODAS las hijas en un único `vehicleOccupantsByIds` network-only — aquí
    // repetirlo añadiría A requests. En la navegación DIRECTA al resumen no
    // hay ese batch previo, así que el warm-up network-only garantiza la
    // frescura que antes daban los per-row (los ocupantes cambian al editar
    // operarios/vehículos sin cambiar los ids).
    if (!opts?.preferCache && action.vehicleOccupantIDs.length > 0) {
      await warmupVehicleOccupantsByIds(
        action.vehicleOccupantIDs,
        false,
        'network-only'
      );
    }
    const transportVehicles = await Promise.all(
      action.vehicleOccupantIDs.map(async (vehicleOccupantId: string) => {
        logSelectorVehicle('🔄 Processing vehicleOccupantId:', vehicleOccupantId);

        try {
          const {
            data: { vehicleOccupant },
          } = await client.query({
            query: GET_VEHICLE_OCCUPANT_BY_ID,
            variables: {
              id: vehicleOccupantId,
            },
            // Hit garantizado tras el warm-up (batch propio o el del detalle
            // — selection-set idéntico + redirect Query.vehicleOccupant); si
            // ningún warm-up pobló la caché (offline, backend sin batch),
            // cache-first hace miss y va a red = comportamiento de hoy.
            fetchPolicy: 'cache-first',
          });

          logSelectorVehicle(
            '📦 VehicleOccupant data:',
            JSON.stringify({
              id: vehicleOccupantId,
              vehicleIDs: vehicleOccupant.vehicleIDs,
              userDriverIDs: vehicleOccupant.userDriverIDs,
            })
          );

          if (!vehicleOccupant.vehicleIDs?.[0]) {
            logSelectorVehicle(
              '⚠️ No vehicle ID found for occupant:',
              vehicleOccupantId
            );
            return null;
          }

          if (!vehicleOccupant.userDriverIDs?.[0]) {
            logSelectorVehicle(
              '⚠️ No driver ID found for occupant:',
              vehicleOccupantId
            );
            return null;
          }

          const {
            data: { vehicle },
          } = await client.query({
            query: GET_VEHICLE_BY_ID,
            variables: {
              id: vehicleOccupant.vehicleIDs[0],
            },
            // Display de catálogo (plate/model/category/ownership/usage) —
            // cache-first dedupe ocupantes que comparten vehículo. OJO: el
            // selection-set pide `accidentIDs`, que FETCH_VEHICLES no trae →
            // el primer fetch de cada vehículo distinto va a red igualmente
            // (miss parcial); los siguientes dan hit. Sin regresión.
            fetchPolicy: 'cache-first',
          });

          logSelectorVehicle(
            '🚗 Vehicle data:',
            JSON.stringify({
              id: vehicle.id,
              plate: vehicle.plate,
              concessionIDs: vehicle.concessionIDs,
            })
          );

          const {
            data: { user },
          } = await client.query({
            query: GET_USER_BY_ID,
            variables: {
              input: {
                id: vehicleOccupant.userDriverIDs[0],
              },
            },
            // Display "conductor" (nombre) — mismo razonamiento que los
            // operarios: dato maestro FetchUsers, hit vía redirect Query.user.
            fetchPolicy: 'cache-first',
          });

          logSelectorVehicle(
            '👤 Driver data:',
            JSON.stringify({
              id: user.id,
              name: user.name,
              surname: user.surName,
            })
          );

          let concessionName;

          if (vehicle?.concessionIDs?.length) {
            const {
              data: { concession },
            } = await client.query({
              query: GET_CONCESSION_BY_ID,
              variables: {
                id: vehicle.concessionIDs[0],
              },
              // Display de nombre de concesión del vehículo — mismo
              // razonamiento que la concesión del operario (hit garantizado
              // vía GET_CONCESSIONS + redirect Query.concession).
              fetchPolicy: 'cache-first',
            });

            concessionName = concession.name;
            logSelectorVehicle(
              '🏢 Concession data:',
              JSON.stringify({
                id: vehicle.concessionIDs[0],
                name: concessionName,
              })
            );
          } else {
            logSelectorVehicle('⚠️ No concession found for vehicle:', vehicle.id);
          }

          const vehicleData = {
            mat: vehicle.plate,
            driver: `${user.name} ${user.surName}`,
            concession: concessionName,
            usage: vehicle.usage, // ✅ Añadir usage
            model: vehicle.model,
            category: vehicle.category,
            ownership: vehicle.ownership,
            teams: vehicle.teams?.map((t: any) => t.name) || [], // ✅ M2M: Equipos del vehículo
            teamIDs: vehicle.teamIDs || [], // ✅ M2M: IDs de equipos
          };

          logSelectorVehicle(
            '✅ Successfully processed vehicle:',
            JSON.stringify(vehicleData)
          );
          return vehicleData;
        } catch (error) {
          console.error(
            '❌ Error processing vehicle occupant:',
            vehicleOccupantId,
            error
          );
          return null;
        }
      })
    );

    // Filter out any null values from failed processing (transport vehicles)
    const validTransportVehicles = transportVehicles.filter(
      (vehicle) => vehicle !== null
    );
    logSelectorVehicle(
      '📊 Final transport vehicles count:',
      validTransportVehicles.length
    );
    logSelectorVehicle(
      '📊 Final transport vehicles data:',
      JSON.stringify(validTransportVehicles)
    );

    // ========== CARGAR VEHÍCULOS DE OBRA (CONSTRUCTION, sin conductor) ==========
    const workVehicles = await Promise.all(
      (action.workVehicleIDs || []).map(async (workVehicleId: string) => {
        logSelectorVehicle('🔄 Processing workVehicleId:', workVehicleId);

        try {
          const {
            data: { vehicle },
          } = await client.query({
            query: GET_VEHICLE_BY_ID,
            variables: {
              id: workVehicleId,
            },
            // Display de catálogo (vehículo de obra) — mismo razonamiento que
            // los vehículos de transporte de arriba.
            fetchPolicy: 'cache-first',
          });

          logSelectorVehicle(
            '🚧 Work vehicle data:',
            JSON.stringify({
              id: vehicle.id,
              plate: vehicle.plate,
              model: vehicle.model,
              usage: vehicle.usage,
            })
          );

          const vehicleData = {
            mat: vehicle.plate,
            model: vehicle.model,
            category: vehicle.category,
            ownership: vehicle.ownership,
            usage: vehicle.usage, // ✅ CONSTRUCTION
            teams: vehicle.teams?.map((t: any) => t.name) || [], // ✅ M2M: Equipos del vehículo
            teamIDs: vehicle.teamIDs || [], // ✅ M2M: IDs de equipos
            // No tiene conductor ni concesión
          };

          logSelectorVehicle(
            '✅ Successfully processed work vehicle:',
            JSON.stringify(vehicleData)
          );
          return vehicleData;
        } catch (error) {
          console.error(
            '❌ Error processing work vehicle:',
            workVehicleId,
            error
          );
          return null;
        }
      })
    );

    const validWorkVehicles = workVehicles.filter(
      (vehicle) => vehicle !== null
    );
    logSelectorVehicle('📊 Final work vehicles count:', validWorkVehicles.length);
    logSelectorVehicle(
      '📊 Final work vehicles data:',
      JSON.stringify(validWorkVehicles)
    );

    // ✅ COMBINAR ambos tipos de vehículos
    const validVehicles = [...validTransportVehicles, ...validWorkVehicles];

    // ¿Actuació bàsica? `containerType` es el camino barato (denormalizado, ya
    // en el fragment). Cuando falta, se resuelve por el tipo del padre.
    //
    // Ese respaldo NO es para casos raros: medido el 2026-08-01, en producción
    // `containerType` está vacío en las 15.334 Orders hijas —el backfill nunca
    // se corrió allí— frente a 942 de 944 en beta. Sin respaldo, en producción
    // el resumen trataría como normal al 100% de las básicas mientras las
    // pestañas sí salen reducidas: secciones de operarios que no tocan y una
    // pre-validación que apunta a una pestaña que el usuario no ve.
    //
    // El coste es una query, y solo cuando el campo falta.
    const parentOrderIdOfAction = order.parentExpeditionOrderIDs?.[0] ?? null;
    let parentOrderTypeOfAction: string | null = null;
    if (!order.containerType && parentOrderIdOfAction) {
      try {
        const { data } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id: parentOrderIdOfAction },
          fetchPolicy: freshOrCache,
        });
        parentOrderTypeOfAction = data?.order?.type ?? null;
      } catch {
        /* sin padre resoluble → se comporta como normal, nunca rompe */
      }
    }
    logSelectorVehicle('📊 Total vehicles count:', validVehicles.length);
    logSelectorVehicle('📊 Total vehicles data:', JSON.stringify(validVehicles));

    // Nombres de quién creó y quién registró. En paralelo y tolerante a fallos:
    // un nombre que no resuelve deja la fila fuera, nunca tumba el resumen —
    // mismo criterio que el resto de resoluciones opcionales de este selector.
    const nombreDeUsuario = async (id?: string | null): Promise<string> => {
      if (!id) return '';
      try {
        const { data } = await client.query({
          query: GET_USER_BY_ID,
          variables: { input: { id } },
          // Dato maestro que `FetchUsers` ya trae: normalmente ni sale a red.
          fetchPolicy: 'cache-first',
        });
        return formatUserFullName(data?.user);
      } catch {
        return '';
      }
    };
    const [creatorName, registeredByName] = await Promise.all([
      nombreDeUsuario(order.userId),
      nombreDeUsuario(order.registeredByUserId),
    ]);

    const result = {
      id: action.id,
      orderId: order.id,
      state: order.state,
      indexExpedition: order.indexExpedition,
      roid:
        typeof order.roid === 'string'
          ? order.roid.split('.')
          : Array.isArray(order.roid)
            ? order.roid
            : [],
      date: order.registerInitDate,
      concession: concession?.name ?? null,
      pkInit: pkFormatter(action.pkInitKm, action.pkInitMeter, true),
      pkEnd: pkFormatter(action.pkEndKm, action.pkEndMeter, true),
      track: trackLabel(action.track),
      direction: action.direction,
      margin: action.margin,
      lane: laneLabel(action.lane),
      capitol: capitol?.name ?? null,
      subCapitol: subCapitol?.name ?? null,
      description: action.descriptionValidated || order.description || null,
      // Bug descripciones (2026-06-05): el resumen muestra DOS filas condicionales
      // (validada 1ª + operario 2ª, solo si tienen valor). Exponemos ambos crudos.
      descriptionValidated: action.descriptionValidated || null,
      operatorDescription: order.description || null,
      // Quién creó el registro y quién lo rellenó. La ficha (ShowOT) ya los
      // enseñaba y el resumen no, así que al validar no había forma de saber
      // quién había metido los datos — que es justo lo que se está revisando.
      creatorName,
      registeredByName,
      notes: order.notes,
      animals,
      activityValue: activityValue ? activityValue.name : undefined,
      assetValue: assetValue ? assetValue.code : undefined,
      assetSwitch: action.assetSwitch,
      action,
      materialsValue,
      machineryValue,
      budgetItemsValue,
      road: road?.name ?? null,
      roadDirections: road?.directions ?? [],
      city: concession?.location ?? null,
      registerInitDate: order.registerInitDate,
      registerEndDate: order.registerEndDate,
      // Hora de llegada: Tipo C exclusivo de Action (no existe en Order).
      registerArrivalDate: action.registerArrivalDate,
      minutesTotals: action.minutesTotals,
      // C-11.13: optional chain — `elementOrder` puede ser undefined si la
      // action no tiene elementOrderIDs (guard de C-11.11). Sin esto el
      // summary crasheaba con `Cannot read properties of undefined (reading 'name')`.
      elementOrder: elementOrder?.name,
      sketch: sketch,
      images: images,
      centerMap: centerMap,
      path: path,
      pointMap: pointMap,
      operators,
      vehicles: validVehicles,
      works,
      isExternal: action.isExternal,
      isSimultaneous: action.isSimultaneous ?? false,
      // Actuació bàsica (hija de un Recorregut de Vigilància): el resumen le
      // oculta las secciones de operarios/horas/maquinaria y se salta la
      // pre-validación del schema de operarios, que si no rebotaría a una
      // pestaña oculta. Discriminador resuelto arriba (containerType + respaldo
      // por tipo del padre — imprescindible en producción, ver el comentario).
      isBasicAction: isBasicAction({
        containerType: order.containerType,
        parentOrderType: parentOrderTypeOfAction,
      }),
      parentOrderIdFromData: parentOrderIdOfAction,
      geolocation:
        geolocationValues &&
          geolocationValues.latitude &&
          geolocationValues.longitude
          ? {
            lat: geolocationValues.latitude,
            lng: geolocationValues.longitude,
          }
          : null,
      externalOperatorsCount,
    };

    logSelector(`✅ [RESULT] Datos retornados:`);
    logSelector(
      `  - Sketch: ${sketch ? `SÍ (${(sketch as ImageData).name})` : 'NO'}`
    );
    logSelector(`  - Imágenes: ${images.length}`);
    logSelector(`  - Operadores: ${operators.length}`);
    logSelector(`  - Vehículos: ${validVehicles.length}`);
    logSelector(`  - Partidas Presupuestarias: ${budgetItemsValue.length}`);
    logSelector(`  - action.userIDs: ${action.userIDs.length}`);
    logSelector(
      `  - action.vehicleOccupantIDs: ${action.vehicleOccupantIDs.length}`
    );
    logSelector(
      `  - action.actionBudgetItemIDs: ${action.actionBudgetItemIDs?.length || 0}`
    );
    logSelector(`🏁 [END] selectActionById completado`);

    return result;
  } catch (err) {
    logSelector(`❌ [ERROR] en selectActionById:`, err);
    Sentry.captureException(err);
    log(err);
  }
};

export const selectActionOrders = async (actions: any[] = [], reFetch = false) => {
  // Las etiquetas de Sentido, Tipo de vía y Carril se calculan aquí y quedan
  // CONGELADAS en la fila, así que hay que tenerlas traducidas antes de
  // empezar: si no, la celda enseña la clave en crudo y ya no se recupera.
  // Ver `utils/i18nReady.ts`.
  await ensureLabelNamespaces();

  logActions('🔍 [selectActionOrders] Iniciando con', {
    actionsCount: actions.length,
    reFetch,
  });

  // Resolver creador (Order.userId → "Nombre Apellido") leyendo FETCH_USERS
  // de la caché Apollo. El loader pre-carga FETCH_USERS antes de invocar este
  // selector, así que readQuery da hit. Patrón espejo del usado en
  // selectMultimediaOrders (src/gql/selectors/multimedia.ts:42-69).
  // creatorMap: nombre + apellido completo para la columna Creador.
  // operatorNameMap: nombre + primer apellido para la columna Operarios. Compromiso
  // entre identificación (nombre solo es ambiguo si hay varios con el mismo) y
  // espacio (el apellido completo satura cuando hay varios operarios concatenados).
  let creatorMap: Record<string, string> = {};
  let operatorNameMap: Record<string, string> = {};
  try {
    const usersData = client.readQuery({ query: FETCH_USERS });
    if (usersData?.users) {
      usersData.users.forEach(
        (u: { id: string; name?: string; surName?: string }) => {
          creatorMap[u.id] = `${u.name ?? ''} ${u.surName ?? ''}`.trim();
          const firstSurName = (u.surName ?? '').split(' ')[0] ?? '';
          operatorNameMap[u.id] = `${u.name ?? ''} ${firstSurName}`.trim();
        },
      );
    }
  } catch {
    // Cache no disponible — columnas Creador/Operarios quedarán vacías como fallback
  }

  // Warm-up: UNA query `ordersByIds` puebla la caché con las Orders paralelas
  // de todas las filas; los GET_ORDER_BY_ID per-row de abajo pasan a
  // resolverse de caché (redirect Query.order). Si falla → no-op, per-row
  // intacto como fallback natural.
  await warmupOrdersByIds(
    actions.map((a) => a?.orderIDs?.[0]),
    reFetch
  );

  // Warm-up de la columna "Empresa externa": 2 queries batch
  // (externalWorksByIds → externalCompaniesByIds) pueblan la caché; el bucle
  // per-row de abajo pasa a resolverse de caché (redirects
  // Query.externalWork/externalCompany). Si falla → no-op, per-row intacto.
  await warmupExternalWorks(
    actions.map((a) => a?.externalWorksIDs ?? []),
    reFetch
  );

  // Ids de catálogo que son corte de carril: SOLO si alguna fila carece del
  // campo `isLaneCut` del servidor. Con datos ya sincronizados esto ahorra las
  // dos queries del catálogo (FETCH_ACTIVITYS + FETCH_PREDEFINED_ORDERS) en
  // cada listado; el respaldo local solo hace falta para lo no sincronizado.
  const laneCutIds = needsLaneCutFallback(actions)
    ? await loadLaneCutIds()
    : EMPTY_LANE_CUT_IDS;

  // Nombres de los materiales para la columna. UNA sola lectura del catálogo
  // por listado —no una por fila— y solo si alguna fila tiene materiales que
  // pintar, igual que el respaldo de corte de carril. `fetchMaterialsCatalog`
  // es `cache-only` en la primera lectura, así que sin cobertura no lanza.
  const materialNameById = needsMaterialsCatalog(actions)
    ? buildMaterialNameMap(await fetchMaterialsCatalog())
    : {};

  return Promise.all(
    actions.map(async (action, index) => {
      try {
        logActions(
          `📋 [selectActionOrders] Procesando action ${index + 1}/${actions.length}:`,
          {
            actionId: action.id,
            orderIDs: action.orderIDs,
          }
        );

        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: action.orderIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        });

        logActions(`📦 [selectActionOrders] Order obtenida:`, {
          orderId: order?.id,
          orderExists: !!order,
          capitolIDs: order?.capitolIDs,
          subCapitolIDs: order?.subCapitolIDs,
        });

        // Resiliencia ante datos huérfanos: la Order padre puede resolver a null
        // (Action huérfana cuyo Order fue borrado → GET_ORDER_BY_ID = null). Antes
        // se lanzaba un Error que el catch de abajo re-lanzaba, vaciando TODA la
        // tabla por una sola fila corrupta. Ahora se omite la fila (se filtra
        // abajo) y se deja traza para depurar la integridad de datos.
        if (!order) {
          console.warn(
            `[selectActionOrders] Order padre no resuelta (huérfana), fila omitida — actionId=${action.id} orderId=${action.orderIDs?.[0]}`,
          );
          return null;
        }

        // Capítulo/subcapítulo AUSENTES no son un error: son un estado normal
        // del ciclo de vida. La actuación básica del Recorrido se crea
        // automáticamente desde el tramo, que no tiene capítulo que heredar, y
        // no lo recibe hasta que alguien pasa por ese paso del wizard (el
        // update lo escribe con `capitols: {set: [], connect}`).
        //
        // Antes se lanzaba un Error aquí y el catch por-fila descartaba la
        // actuación entera: desaparecía de la tabla Y del Excel sin decir nada,
        // y el `totalCount` del backend —que sí la cuenta— dejaba de cuadrar
        // con las filas pintadas (medido en beta: 15 de 25 pintadas). Peor: una
        // básica ANULADA antes de completarse ya nunca pasa por el wizard, así
        // que quedaba invisible para siempre.
        //
        // Medido en beta antes de cambiarlo: de 86 actuaciones `partial`, 77
        // tienen capítulo (74 genéricas + 3 del RV) — o sea que "sin capítulo"
        // no es sinónimo de borrador, es solo la básica del RV recién nacida.
        // Y de las del RV ya completadas, open 5/5 y validated 6/6 lo tienen:
        // el hueco se rellena solo.
        //
        // Ahora la celda sale vacía y la fila se ve, que es lo que el usuario
        // necesita: saber qué básicas están a medias.
        const capitol = order.capitolIDs?.[0]
          ? (
              await client.query({
                query: GET_CAPITOL_BY_ID,
                variables: {
                  id: order.capitolIDs[0],
                },
                fetchPolicy: reFetch ? 'network-only' : 'cache-first',
              })
            ).data?.capitol
          : null;

        const subCapitol = order.subCapitolIDs?.[0]
          ? (
              await client.query({
                query: GET_SUB_CAPITOL_BY_ID,
                variables: {
                  id: order.subCapitolIDs[0],
                },
                fetchPolicy: reFetch ? 'network-only' : 'cache-first',
              })
            ).data?.subCapitol
          : null;

        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: {
            id: action.concessionIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        });

        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: {
            id: action.roadIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        });

        // Resolve external companies for table display
        let externalCompanies: string[] = [];
        if (action.externalWorksIDs && action.externalWorksIDs.length > 0) {
          const companyNames = await Promise.all(
            action.externalWorksIDs.map(async (workId: string) => {
              const { data: { externalWork } } = await client.query({
                query: GET_EXTERNAL_WORK_BY_ID,
                variables: { id: workId },
                fetchPolicy: reFetch ? 'network-only' : 'cache-first',
              });
              if (externalWork.externalCompanyIDs?.[0]) {
                const { data: { externalCompany } } = await client.query({
                  query: GET_EXTERNAL_COMPANY_BY_ID,
                  variables: { id: externalWork.externalCompanyIDs[0] },
                  fetchPolicy: reFetch ? 'network-only' : 'cache-first',
                });
                return externalCompany.companyName;
              }
              return null;
            })
          );
          externalCompanies = companyNames.filter(Boolean);
        }

        let predefinedOrderName = '';
        const predefinedId = order.predefinedOrderIDs?.[0];
        if (predefinedId) {
          try {
            const cached = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
            predefinedOrderName = cached?.predefinedOrders?.find((p: any) => p.id === predefinedId)?.name ?? '';
          } catch { /* cache not available */ }
        }

        logActions(
          `✅ [selectActionOrders] Action ${action.id} procesada exitosamente`
        );

        // Resolve activity name for table display
        let activityName = '';
        if (action.activityIDs && action.activityIDs.length > 0) {
          try {
            const { data: { activity: activityData } } = await client.query({
              query: GET_ACTIVITY_BY_ID,
              variables: { id: action.activityIDs[0] },
              fetchPolicy: reFetch ? 'network-only' : 'cache-first',
            });
            activityName = activityData?.name ?? '';
          } catch {
            activityName = '';
          }
        }

        return {
          id: order.id,
          roid:
            typeof order.roid === 'string'
              ? order.roid.split('.')
              : Array.isArray(order.roid)
                ? order.roid
                : [],
          registerInitDate: order.registerInitDate,
          registerEndDate: order.registerEndDate,
          // Hora de llegada: Tipo C exclusivo de Action (no existe en Order).
          registerArrivalDate: action.registerArrivalDate,
          creationDate: order.creationDate,
          capitol: capitol?.name ?? '',
          subCapitol: subCapitol?.name ?? '',
          user: order.userId ? (creatorMap[order.userId] ?? '') : '',
          registeredBy: order.registeredByUserId ? (creatorMap[order.registeredByUserId] ?? '') : '',
          operators: (action.userIDs ?? [])
            .map((id: string) => operatorNameMap[id])
            .filter(Boolean)
            .join(', '),
          // La básica del RV no tiene tripulación (es del recorrido): tabla y
          // Excel pintan el guion en «Operarios» en vez de una celda vacía.
          isBasicAction: isBasicAction({ containerType: action.containerType }),
          pkInit: pkFormatter(action.pkInitKm, action.pkInitMeter),
          pkEnd: pkFormatter(action.pkEndKm, action.pkEndMeter),
          concession: concession.name,
          // Bug descripciones (2026-06-05): mostrar la validada si existe, si no la
          // del operario. Antes mostraba order.description crudo → salía vacío/erróneo
          // cuando el texto vivía en descriptionValidated. Regla única en tablas/históricos.
          description: action.descriptionValidated || order.description,
          notes: order.notes,
          state: order.state,
          type: order.type,
          road: road.name,
          // Sentido: mismo helper canónico que usan las otras cuatro tablas de
          // listado, así que el formato "Texto (símbolo)" es idéntico en toda
          // la aplicación. Se formatea AQUÍ y no en la celda porque este
          // selector es el único punto por el que pasan la tabla Y el Excel:
          // formatear en un solo sitio hace imposible que diverjan. `road` ya
          // se consulta arriba y `GET_ROAD_BY_ID` devuelve `directions`, así
          // que no cuesta ni una query nueva.
          direction: formatDirectionCellLabel(action.direction, road?.directions),
          track: trackLabel(action.track),
          // Carril traducido por catálogo, hermano exacto de `track`.
          lane: laneLabel(action.lane),
          activity: activityName,
          // Corte de carril con criterio unificado: flag OR actividad OR
          // OT predefinida. La regla vive entera en `utils/laneCut.ts`.
          // Manda el valor del backend (`Action.isLaneCut`), que es el mismo
          // por el que filtra el servidor. El cálculo local solo cubre la fila
          // creada/editada sin cobertura que aún no ha sincronizado.
          isLaneCut: resolveIsLaneCut(
            action.isLaneCut,
            {
              laneCutting: action.laneCutting,
              activityIDs: action.activityIDs,
              predefinedOrderIDs: action.predefinedOrderIDs,
            },
            laneCutIds,
          ),
          // Nombres resueltos contra el catálogo, NO desnormalizados en la
          // actuación: así renombrar un material en Gestión no deja obsoletas
          // las filas ya guardadas.
          materials: resolveMaterialNames(action.materialIds, materialNameById),
          actionID: action.id,
          // Actuación simultánea (Tipo C de Action): badge en tablas de actuaciones.
          isSimultaneous: action.isSimultaneous ?? false,
          externalCompanies,
          predefinedOrder: predefinedOrderName,
          // Fase 4 nav Trabajo/Revisión (follow-up T4, 2026-07-12) — "Origen"
          // viene directo del campo GraphQL `Action.containerType` (el
          // backend lo denormaliza en Prisma; ver NEW_ACTION fragment). Ya no
          // se deriva por fila con un lookup extra client-side.
          containerType: action.containerType,
        };
      } catch (error) {
        logActions(
          `💥 [selectActionOrders] Error procesando action ${action.id}:`,
          error
        );
        Sentry.captureException(error);
        // Aísla el fallo por-fila: antes `throw error` propagaba y el catch del
        // loader vaciaba TODA la tabla por una sola fila corrupta. Ahora la fila
        // problemática se omite (se filtra abajo) y el resto se renderiza.
        console.warn(
          `[selectActionOrders] fila omitida por error de resolución — actionId=${action.id}`,
          error,
        );
        return null;
      }
    })
  ).then((rows) => rows.filter((row): row is NonNullable<typeof row> => row != null));
};

/* SELECTOR FOR ACTION VALIDATION */

export const selectOperatorsAndVehiclesFormValues = async (
  actionId: string
) => {
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelector('🚀 selectOperatorsAndVehiclesFormValues INICIO');
  logSelector('actionId recibido:', actionId);
  logSelector('═══════════════════════════════════════════════════════════════════');

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 DEBUG - Usando debug library
  // ═══════════════════════════════════════════════════════════════════════════
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelector('🔍 DEBUG selectOperatorsAndVehiclesFormValues');
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelector('📍 actionId:', actionId);

  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  // 🔍 DEBUG: RAW ACTION DATA
  logSelectorData('📦 ACTION RAW DATA:');
  logSelectorData('  - action.id:', action.id);
  logSelectorData('  - action.userIDs:', JSON.stringify(action.userIDs));
  logSelectorData('  - action.userIDs.length:', action.userIDs?.length || 0);
  logSelectorData('  - action.vehicleOccupantIDs:', JSON.stringify(action.vehicleOccupantIDs));
  logSelectorData('  - action.externalWorksIDs:', JSON.stringify(action.externalWorksIDs));

  logSelectorData('📦 Action cargada:');
  logSelectorData('  - action.id:', action.id);
  logSelectorData('  - action.userIDs:', action.userIDs);
  logSelectorData('  - action.userIDs.length:', action.userIDs?.length || 0);
  logSelectorData('  - action.vehicleOccupantIDs:', action.vehicleOccupantIDs);
  logSelectorData('  - action.vehicleOccupantIDs.length:', action.vehicleOccupantIDs?.length || 0);
  logSelectorData('  - action.externalWorksIDs:', action.externalWorksIDs);

  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: action.orderIDs[0],
    },
  });

  logSelectorData('📦 Order cargada:', order.id);

  const {
    data: { concessions },
  } = await client.query({
    query: GET_SELECTORS_DATA,
  });

  logSelectorData('📦 Concessions cargadas:', concessions?.length || 0);

  logSelector('═══ CARGANDO OPERARIOS DE LA ACTION ═══');
  logSelector('Total userIDs a procesar:', action.userIDs?.length || 0);

  const allOperators = await Promise.all(
    action.userIDs.map(async (userID: string, idx: number) => {
      let concessionInnerName;
      logSelectorUser(`[${idx}] Consultando usuario ID: ${userID}`);

      const {
        data: { user },
      } = await client.query({
        query: GET_USER_BY_ID,
        variables: {
          input: {
            id: userID,
          },
        },
      });

      // ⚠️ LOG CRÍTICO: Verificar el valor de internal
      logSelectorUser(`[${idx}] ✅ Usuario cargado:`);
      logSelectorUser(`    - id: ${user.id}`);
      logSelectorUser(`    - name: ${user.name} ${user.surName}`);
      logSelectorUser(`    - internal: ${user.internal} (tipo: ${typeof user.internal})`);
      logSelectorUser(`    - userType: ${user.userType}`);
      logSelectorUser(`    - isActive: ${user.isActive}`);
      logSelectorUser(`    - concessionIDs: ${JSON.stringify(user.concessionIDs)}`);

      if (user.internal === undefined) {
        logSelectorUser(`    ⚠️ ALERTA: internal es undefined para ${user.name}`);
      } else if (user.internal === null) {
        logSelectorUser(`    ⚠️ ALERTA: internal es null para ${user.name}`);
      } else if (user.internal === false) {
        logSelectorUser(`    ℹ️ INFO: internal es false - irá a externalOperatorsTable`);
      } else if (user.internal === true) {
        logSelectorUser(`    ✅ OK: internal es true - irá a internalOperatorsTable`);
      }

      // NOTE: teamIDs removed from user model - always use concessionIDs directly
      // if (user.teamIDs?.[0]) {
      //   const {
      //     data: { team },
      //   } = await client.query({
      //     query: GET_TEAM_BY_ID,
      //     variables: {
      //       id: user.teamIDs[0],
      //     },
      //   });
      //
      //   const {
      //     data: { concession },
      //   } = await client.query({
      //     query: GET_CONCESSION_BY_ID,
      //     variables: {
      //       id: team.concessionIDs[0],
      //     },
      //   });
      //   concessionInnerName = [concession.name];
      // } else {
      if (user.concessionIDs && user.concessionIDs.length > 1) {
        concessionInnerName = concessions.map(
          (concessionInner: { name: string }) => concessionInner.name
        );
      } else {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: {
            id: user.concessionIDs[0],
          },
        });
        concessionInnerName = [concession.name];
      }
      // }

      return {
        ...user,
        concession: concessionInnerName,
      };
    })
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 FILTRADO CRÍTICO POR CAMPO INTERNAL
  // ═══════════════════════════════════════════════════════════════════════════
  logSelector('═══ ANÁLISIS DE OPERARIOS ANTES DEL FILTRADO ═══');
  logSelector('Total operarios en allOperators:', allOperators.length);

  allOperators.forEach((op, idx) => {
    logSelectorUser(`[${idx}] Operario: ${op.name || op.id}`);
    logSelectorUser(`    internal: ${op.internal} (tipo: ${typeof op.internal})`);
    logSelectorUser(`    concessionIDs: ${JSON.stringify(op.concessionIDs)}`);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔧 SIMPLIFICADO: Solo filtrar por userType, el campo 'internal' es informativo
  // MOTIVO: Todos los operarios (userType === 'operator') van a operatorsTable
  // NOTA: externalOperatorsTable ELIMINADO - las empresas externas se manejan en 'externals'
  // ═══════════════════════════════════════════════════════════════════════════
  const operatorsTable = allOperators.filter(
    (operator) => operator.userType === 'operator'
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 RESULTADO DEL FILTRADO (simplificado: solo userType)
  // ═══════════════════════════════════════════════════════════════════════════
  logSelector('═══ RESULTADO DEL FILTRADO POR userType ═══');
  logSelector('operatorsTable.length (userType=operator):', operatorsTable.length);

  if (operatorsTable.length > 0) {
    logSelector('✅ Operarios encontrados:');
    operatorsTable.forEach((op, idx) => {
      logSelector(`  [${idx}] ${op.name} ${op.surName} - userType: ${op.userType}, internal: ${op.internal}`);
    });
  } else {
    logSelector('⚠️ NO HAY OPERARIOS (userType=operator)');
  }

  if (operatorsTable.length === 0 && allOperators.length > 0) {
    logSelector('═══════════════════════════════════════════════════════════════════');
    logSelector('⚠️ allOperators tiene datos pero ninguno tiene userType=operator');
    logSelector('═══════════════════════════════════════════════════════════════════');
  }

  // Log de clasificación
  allOperators.forEach((op, idx) => {
    const isOperator = op.userType === 'operator';
    logSelector(`[${idx}] ${op.name}: userType=${op.userType}, internal=${op.internal} → ${isOperator ? 'INCLUIDO' : 'EXCLUIDO'}`);
  });

  logSelector('═══ CARGANDO VEHÍCULOS DE LA ACTION ═══');
  logSelector('Total vehicleOccupantIDs a procesar:', action.vehicleOccupantIDs?.length || 0);

  const vehicleTableRaw = await Promise.all(
    action.vehicleOccupantIDs.map(async (vehicleOccupantId: string, idx: number) => {
      logSelectorVehicle(`[${idx}] Consultando vehicleOccupant ID: ${vehicleOccupantId}`);

      const {
        data: { vehicleOccupant },
      } = await client.query({
        query: GET_VEHICLE_OCCUPANT_BY_ID,
        variables: {
          id: vehicleOccupantId,
        },
      });

      logSelectorVehicle(`[${idx}] VehicleOccupant cargado:`, vehicleOccupant.id);
      logSelectorVehicle(`    - vehicleIDs: ${JSON.stringify(vehicleOccupant.vehicleIDs)}`);
      logSelectorVehicle(`    - userDriverIDs: ${JSON.stringify(vehicleOccupant.userDriverIDs)}`);

      const {
        data: { vehicle },
      } = await client.query({
        query: GET_VEHICLE_BY_ID,
        variables: {
          id: vehicleOccupant.vehicleIDs[0],
        },
      });

      logSelectorVehicle(`[${idx}] Vehicle cargado: ${vehicle.id} - ${vehicle.plate || vehicle.name}`);

      const {
        data: { user },
      } = await client.query({
        query: GET_USER_BY_ID,
        variables: {
          input: {
            id: vehicleOccupant.userDriverIDs[0],
          },
        },
      });

      logSelectorVehicle(`[${idx}] Driver cargado: ${user.id} - ${user.name} ${user.surName}`);

      return {
        vehicleID: vehicle.id,
        driverID: user.id,
        vehicleOccupantID: vehicleOccupant.id,
        teams: vehicle.teams?.map((t: any) => t.name) || [], // ✅ M2M: Equipos del vehículo
        teamIDs: vehicle.teamIDs || [], // ✅ M2M: IDs de equipos
      };
    })
  );

  logSelector('═══ RESULTADO DE VEHÍCULOS ═══');
  logSelector('vehicleTableRaw.length:', vehicleTableRaw.length);
  vehicleTableRaw.forEach((v, idx) => {
    logSelectorVehicle(`[${idx}] vehicleID: ${v.vehicleID}, driverID: ${v.driverID}`);
  });

  const vehicleTable: Record<string, unknown> = {};

  vehicleTableRaw.forEach((vehicle) => {
    vehicleTable[vehicle.vehicleID] = vehicle;
  });

  const concessionWithUndefined = [
    { id: '', name: 'Tots els operaris' },
    ...concessions,
  ];

  const externalWorks = await Promise.all(
    action.externalWorksIDs.map(async (workId: string) => {
      const {
        data: { externalWork },
      } = await client.query({
        query: GET_EXTERNAL_WORK_BY_ID,
        variables: {
          id: workId,
        },
      });

      const {
        data: { externalCompany },
      } = await client.query({
        query: GET_EXTERNAL_COMPANY_BY_ID,
        variables: {
          id: externalWork.externalCompanyIDs[0],
        },
      });

      return {
        ...externalWork,
        company: externalCompany.companyName,
        // work: externalWork.workName,
        workId,
      };
    })
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 DEBUG - DATOS FINALES QUE SE RETORNAN
  // ═══════════════════════════════════════════════════════════════════════════
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelector('🏁 selectOperatorsAndVehiclesFormValues - DATOS FINALES A RETORNAR');
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelectorData('OPERARIOS (operatorsTable):', operatorsTable.length);
  operatorsTable.forEach((op, idx) => {
    logSelectorData(`  [${idx}] ${op.name} ${op.surName} - ID: ${op.id} - internal: ${op.internal}`);
  });
  logSelectorData('VEHÍCULOS (vehicleTable):', Object.keys(vehicleTable).length);
  Object.entries(vehicleTable).forEach(([key, value]: [string, any]) => {
    logSelectorData(`  [${key}] driverID: ${value?.driverID}`);
  });
  logSelectorData('TRABAJOS EXTERNOS (externalWorks):', externalWorks.length);
  logSelectorData('hoursType:', action.typeOfHour);
  logSelectorData('isExternal:', externalWorks.length > 0);
  logSelector('═══════════════════════════════════════════════════════════════════');

  // 🔍 DEBUG - DATOS FINALES A RETORNAR
  logSelector('═══════════════════════════════════════════════════════════════════');
  logSelector('🏁 DATOS FINALES QUE SE RETORNAN:');
  logSelector('  - operatorsTable:', operatorsTable.length, 'operarios');
  logSelector('  - vehicleTable:', Object.keys(vehicleTable).length, 'vehículos');
  logSelector('  - externalWorks:', externalWorks.length, 'trabajos externos');
  logSelector('  - hoursType:', action.typeOfHour);
  logSelector('  - isExternal:', externalWorks.length > 0);
  logSelector('═══════════════════════════════════════════════════════════════════');

  return {
    hoursType: action.typeOfHour,
    registerInitDate: order.registerInitDate,
    registerEndDate: order.registerEndDate,
    // Hora de llegada: Tipo C exclusivo de Action (no existe en Order).
    registerArrivalDate: action.registerArrivalDate,
    totalHours: `${Math.floor(action.minutesTotals / 60)
      .toString()
      .padStart(2, '0')}:${(action.minutesTotals % 60)
        .toString()
        .padStart(2, '0')}`,
    vehicleTable,
    operatorsTable,
    breadCrumbsData: {
      concession: action.concessionIDs[0],
      concessions: concessionWithUndefined,
    },
    isExternal: externalWorks.length > 0,
    // Actuación simultánea: marca el resumen (total H.P. = 0 por diseño).
    isSimultaneous: action.isSimultaneous ?? false,
    // externalInitDate: action.externalInitDate,
    // externalEndDate: action.externalEndDate,
    // externalTypeOfHour: action.externalTypeOfHour,
    // externalMinutesTotals: action.externalMinutesTotals,
    externalWorks,
  };
};

export const selectLocationValues = async (actionId: string) => {
  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  const [
    {
      data: { concession },
    },
    {
      data: { concessions },
    },
    {
      data: { road },
    },
  ] = await Promise.all([
    queryOptionalById(
      GET_CONCESSION_BY_ID,
      action.concessionIDs?.[0],
      'concession'
    ),
    client.query({
      query: GET_CONCESSIONS,
    }),
    queryOptionalById(GET_ROAD_BY_ID, action.roadIDs?.[0], 'road'),
  ]);

  const {
    data: { readRoads },
  } = concession
    ? await client.query({
        query: GET_ROADS_BY_IDS,
        variables: {
          ids: concession.roadIDs,
        },
      })
    : { data: { readRoads: [] } };

  // C-11.13: cargar lat/lng del GeoPositionAction asociado (si existe) para
  // que el botón "marcar geoposición" del LocationForm aparezca en verde al
  // editar una action ya geoposicionada. Sin esto el form arrancaba con
  // `actionCoordinates` vacío → botón negro → usuario clicaba pensando que
  // creaba una nueva geoposición y al guardar las coords NO se persistían
  // (saveData de edit no leía el campo).
  let actionCoordinates: string | undefined;
  if (action.geoPositionActionIDs?.[0]) {
    try {
      const {
        data: { geoPositionAction },
      } = await client.query({
        query: GET_POSITION_ACTIONS,
        variables: { id: action.geoPositionActionIDs[0] },
      });
      if (
        geoPositionAction?.latitude != null &&
        geoPositionAction?.longitude != null
      ) {
        actionCoordinates = JSON.stringify({
          lat: geoPositionAction.latitude,
          lng: geoPositionAction.longitude,
        });
      }
    } catch {
      // noop — si la query falla seguimos sin coords (botón negro). El usuario
      // puede re-marcar la geoposición.
    }
  }

  return {
    actionConcession: concession?.id ?? '',
    concessions,
    actionCity: concession?.location ?? '',
    actionPkInitRoad: road
      ? pkFormatter(road.pkInitKm, road.pkInitMeter, true)
      : '',
    actionPkEndRoad: road ? pkFormatter(road.pkEndKm, road.pkEndMeter, true) : '',
    actionRoad: road?.id ?? '',
    roads: readRoads,
    actionPkInit: pkFormatter(action.pkInitKm, action.pkInitMeter, true),
    actionPkEnd: pkFormatter(action.pkEndKm, action.pkEndMeter, true),
    groupTracks,
    actionTrack: action.track,
    actionDirection: action.direction ?? undefined,
    actionMargin: action.margin ?? undefined,
    groupLanes,
    actionLane: action.lane,
    actionCoordinates,
  };
};

export const selectEditionValues = async (actionId: string) => {
  const logEdition = debug('rois:selector:action:edition');
  if (import.meta.env.DEV) {
    debug.enable('rois:selector:action:*');
  }

  logEdition(`🔍 [START] selectEditionValues para actionId: ${actionId}`);

  const images: ImageData[] = [];
  let sketch: ImageData | null = null;

  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  logEdition(`📋 Action obtenida: ${action.id}, orderIDs: ${action.orderIDs}`);

  const {
    data: { order },
  } = await client.query({
    query: GET_ORDER_BY_ID,
    variables: {
      id: action.orderIDs[0],
    },
  });

  logEdition(
    `📦 Order obtenida: ${order.id}, imageIDs count: ${order.imageIDs?.length || 0}`
  );

  if (order.imageIDs.length > 0) {
    logEdition(
      `🖼️  Procesando ${order.imageIDs.length} imágenes para edición...`
    );

    // C-11.13: ver comentario equivalente en `selectActionById`. Mismo patrón
    // de defensa: recolectar candidatos y elegir el sketch más reciente por
    // `creatingDate` para que el form de edit cargue siempre el último
    // (incluso si por regresión hubiera duplicados en BD).
    const sketchCandidates: Array<{
      id: string;
      databaseId: string;
      creatingDate: string;
      name: string;
    }> = [];

    await Promise.all(
      order.imageIDs.map(async (imageId: string) => {
        const {
          data: { image },
        } = await client.query({
          query: GET_IMAGE_BY_ID,
          variables: {
            id: imageId,
          },
        });

        if (!image) return;

        logEdition(`  📸 Imagen: ${image.name} (ID: ${image.id})`);

        if (image.name.includes('_canvas')) {
          sketchCandidates.push({
            id: image.id,
            databaseId: image.id, // T56 FIX: Preservar ID de BD
            creatingDate: image.creatingDate,
            name: image.name,
          });
          logEdition(`  ✅ SKETCH DETECTADO para edición: ${image.name}`);
        } else {
          images.push({
            id: image.id,
            date: image.creatingDate,
            name: image.name,
          });
          logEdition(`  📷 Imagen normal añadida para edición: ${image.name}`);
        }
      })
    );

    if (sketchCandidates.length > 0) {
      sketchCandidates.sort(
        (a, b) =>
          new Date(b.creatingDate).getTime() -
          new Date(a.creatingDate).getTime(),
      );
      const latest = sketchCandidates[0];
      sketch = {
        id: latest.id,
        databaseId: latest.databaseId,
        date: latest.creatingDate,
        name: latest.name,
      };
      if (sketchCandidates.length > 1) {
        logEdition(
          `  ⚠️ ${sketchCandidates.length} sketches encontrados, usando el más reciente: ${latest.name}`,
        );
      }
    }

    logEdition(
      `📊 Resumen edición: ${images.length} imagen(es) normal(es), ${sketch ? '1 sketch' : 'sin sketch'}`
    );
  } else {
    logEdition(`⚠️  No hay imágenes en la orden para edición`);
  }

  const [
    {
      data: { activity },
    },
    {
      data: { capitol },
    },
    {
      data: { subCapitol },
    },
    {
      data: { elementOrder },
    },
    {
      data: { capitols },
    },
    {
      data: { predefinedOrders },
    },
  ] = await Promise.all([
    // Una básica del recorrido a medias no tiene actividad, capítulo ni
    // subcapítulo: la pestaña abre vacía para completarla, en vez de reventar.
    queryOptionalById(GET_ACTIVITY_BY_ID, action.activityIDs?.[0], 'activity'),
    queryOptionalById(GET_CAPITOL_BY_ID, order.capitolIDs?.[0], 'capitol'),
    queryOptionalById(
      GET_SUB_CAPITOL_BY_ID,
      order.subCapitolIDs?.[0],
      'subCapitol'
    ),
    // If the order is a partial action, order.elementOrderIDs
    // is an empty array, so return undefined
    order.elementOrderIDs.length > 0
      ? client.query({
        query: GET_ELEMENT_ORDER_BY_ID,
        variables: {
          id: order.elementOrderIDs[0],
        },
      })
      : Promise.resolve({ data: { elementOrder: undefined } }),
    client.query({
      query: FETCH_CAPITOLS,
    }),
    client.query({
      query: FETCH_PREDEFINED_ORDERS,
    }),
  ]);
  // Sin capítulo (o subcapítulo) no hay lista hija que pedir.
  const {
    data: { readSubCapitols },
  } = capitol
    ? await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: capitol.subCapitolsIDs,
        },
      })
    : { data: { readSubCapitols: [] } };

  const {
    data: { readElementOrders },
  } = subCapitol
    ? await client.query({
        query: GET_ELEMENT_ORDERS_BY_ID,
        variables: {
          ids: subCapitol.elementOrdersIds,
        },
      })
    : { data: { readElementOrders: [] } };

  let assetValue;

  if (action.assetIDs.length > 0) {
    assetValue = await client.query({
      query: GET_ASSET_BY_ID,
      variables: {
        id: action.assetIDs[0],
      },
    });
  }

  const result = {
    description: action.descriptionValidated
      ? action.descriptionValidated
      : order.description,
    editionDescription: action.descriptionValidated ?? '',
    editionAssetSwitch: action.assetSwitch ? 'true' : 'false',
    editionAsset: assetValue ? assetValue.data.asset.id : '',
    editionCapitol: capitol?.id ?? '',
    editionSubCapitol: subCapitol?.id ?? '',
    editionActivity: activity?.id ?? '',
    editionElementOrder: elementOrder?.id,
    editionLaneCutting: action.laneCutting ? 'true' : 'false',
    capitols,
    predefinedOrders,
    editionPredefine: action.predefinedOrderIDs?.[0],
    subCapitols: readSubCapitols,
    elementOrders: readElementOrders,
    editionSketch: sketch ? JSON.stringify(sketch) : null,
    editionImages: JSON.stringify(images),
    // Operario que registra (editable en validación, no obligatorio). `?? ''`
    // deja el dropdown vacío en históricos sin operario. Entra en el objeto que
    // el loader usa como `dataFromServer`, así que el diff de AnimalsForm solo
    // lo incluye en el save cuando el usuario cambia de operario de verdad.
    registeredByUserId: order.registeredByUserId ?? '',
  };

  logEdition(`✅ [RESULT] Datos de edición retornados:`);
  logEdition(
    `  - editionSketch: ${result.editionSketch ? 'SÍ (con datos)' : 'NO (null)'}`
  );
  logEdition(`  - editionImages: ${images.length} imagen(es)`);
  logEdition(`  - editionActivity: ${result.editionActivity}`);
  logEdition(`🏁 [END] selectEditionValues completado`);

  return result;
};

export const selectMaterialsValue = async (actionId: string) => {
  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  const materialsTable: { [key: string]: MaterialsType } = {};

  if (action.materialUnitIDs.length > 0) {
    for (const materialUnitId of action.materialUnitIDs) {
      const {
        data: { materialUnit },
      } = await client.query({
        query: GET_MATERIAL_UNIT_BY_ID,
        variables: {
          id: materialUnitId,
        },
      });
      const {
        data: { material },
      } = await client.query({
        query: GET_MATERIAL_BY_ID,
        variables: {
          id: materialUnit.materialIDs[0],
        },
      });

      const materialId = material.id;

      materialsTable[materialId] = {
        ...material,
        materialUnitId: materialUnit.id,
        ut: `${materialUnit.unit}`,
      };
    }
  }

  // ✅ FIX Task 10: Retornar 'true' solo si hay materiales, 'false' si está vacío
  // Esto hace que el switch muestre "No hay" cuando no hay datos en ActionsValidation
  const hasMaterials = Object.keys(materialsTable).length > 0;
  return {
    isMaterialTableDisplayed: hasMaterials ? 'true' : 'false',
    materialsTable,
  };
};

export const selectBudgetItemsValue = async (actionId: string) => {
  const logBudgetSelector = debug('rois:budgetitems:selector');

  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  logBudgetSelector(
    'Fetching budget items for action %s, found %d items',
    actionId,
    action.actionBudgetItemIDs?.length || 0
  );

  const budgetItemsTable: { [key: string]: any } = {};

  if (action.actionBudgetItemIDs && action.actionBudgetItemIDs.length > 0) {
    for (const actionBudgetItemId of action.actionBudgetItemIDs) {
      const {
        data: { actionBudgetItem },
      } = await client.query({
        query: GET_ACTION_BUDGET_ITEM,
        variables: {
          id: actionBudgetItemId,
        },
      });

      const {
        data: { budgetItem },
      } = await client.query({
        query: GET_BUDGET_ITEM,
        variables: {
          id: actionBudgetItem.budgetItemId,
        },
      });

      const {
        data: { budgetCategory },
      } = await client.query({
        query: GET_BUDGET_CATEGORY,
        variables: {
          id: budgetItem.budgetCategoryId,
        },
      });

      const budgetItemId = budgetItem.id;

      budgetItemsTable[budgetItemId] = {
        id: actionBudgetItem.id,
        budgetItemId: budgetItem.id,
        code: budgetItem.code,
        description: budgetItem.description,
        unit: budgetItem.unit,
        quantity: actionBudgetItem.quantity,
        unitPrice: budgetItem.unitPrice,
        totalAmount: actionBudgetItem.totalAmount,
        category: {
          code: budgetCategory.code,
          name: budgetCategory.name,
          color: budgetCategory.color,
        },
      };
    }
  }

  const itemCount = Object.keys(budgetItemsTable).length;
  logBudgetSelector('Budget items processed: %d items', itemCount);

  // ✅ FIX Task 10: Retornar 'true' solo si hay partidas, 'false' si está vacío
  // Esto hace que el switch muestre "No hay" cuando no hay datos en ActionsValidation
  const hasBudgetItems = Object.keys(budgetItemsTable).length > 0;
  return {
    isBudgetItemsTableDisplayed: hasBudgetItems ? 'true' : 'false',
    budgetItemsTable,
  };
};

export const selectMachineriesValue = async (actionId: string) => {
  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  const machineryTable: { [key: string]: MachineryTypes } = {};
  const vehiclesTable: { [key: string]: any } = {};

  // ========== CARGAR MAQUINARIA AUXILIAR ==========
  if (action.auxMachineUnitIDs.length > 0) {
    for (const auxMachineUnitId of action.auxMachineUnitIDs) {
      const {
        data: { auxMachineUnit },
      } = await client.query({
        query: GET_AUX_MACHINE_UNIT_BY_ID,
        variables: {
          id: auxMachineUnitId,
        },
      });

      const {
        data: { auxMachine },
      } = await client.query({
        query: GET_AUX_MACHINE_BY_ID,
        variables: {
          id: auxMachineUnit.auxMachineIDs[0],
        },
      });

      const machineryId = auxMachine.id;

      machineryTable[machineryId] = {
        ...auxMachine,
        auxMachineUnitId: auxMachineUnit.id,
        ut: `${auxMachineUnit.unit}`,
      };
    }
  }

  // ========== FIX BUG #55: Cargar teams para resolver filtro por equipo ==========
  // Construir mapa vehicleID → teams (M2M) igual que en los loaders y selectActiveOperatorsAndVehicles
  const vehicleTeamMap = new Map<string, Array<{ id: string; name: string }>>();
  try {
    const {
      data: { teams },
    } = await client.query({
      query: FETCH_TEAMS,
      fetchPolicy: 'cache-first', // Usar cache ya que los loaders lo traen con network-only
    });

    (teams || []).forEach((team: { id: string; name: string; vehicleIDs: string[] }) => {
      (team.vehicleIDs || []).forEach((vehicleId: string) => {
        const existing = vehicleTeamMap.get(vehicleId) || [];
        vehicleTeamMap.set(vehicleId, [...existing, { id: team.id, name: team.name }]);
      });
    });
  } catch (error) {
    console.error('Error loading teams for machinery vehicles:', error);
  }

  // ========== CARGAR VEHÍCULOS DE OBRA (CONSTRUCTION) ==========
  // ✅ Ahora se cargan directamente desde workVehicleIDs, sin VehicleOccupant
  if (action.workVehicleIDs && action.workVehicleIDs.length > 0) {
    for (const workVehicleId of action.workVehicleIDs) {
      try {
        const {
          data: { vehicle },
        } = await client.query({
          query: GET_VEHICLE_BY_ID,
          variables: {
            id: workVehicleId,
          },
        });

        const vehicleId = vehicle.id;

        // ✅ FIX BUG #55: Resolver teams del vehículo desde vehicleTeamMap
        const vehicleTeams = vehicleTeamMap.get(vehicleId) || [];

        vehiclesTable[vehicleId] = {
          id: vehicle.id,
          vehicleId: vehicle.id,
          plate: vehicle.plate,
          model: vehicle.model,
          category: vehicle.category,
          ownership: vehicle.ownership,
          usage: vehicle.usage,
          teams: vehicleTeams.map((t) => t.name),
          teamIDs: vehicleTeams.map((t) => t.id),
        };
      } catch (error) {
        console.error(`Error loading work vehicle ${workVehicleId}:`, error);
      }
    }
  }

  // El switch de equipamiento representa "¿tenemos Maquinaria Auxiliar?" y
  // arranca ABIERTO solo si la actuación ya tiene maquinaria auxiliar guardada.
  // Los vehículos de obra se cargan igualmente en vehiclesTable (se ven al abrir
  // y se persisten por el guardado change-based, independiente del switch).
  // El sessionStorage del formulario sobrescribe este valor si el usuario lo
  // cambia manualmente durante la edición.
  const hasAuxiliaryMachinery = Object.keys(machineryTable ?? {}).length > 0;
  const equipmentSwitch = hasAuxiliaryMachinery ? 'true' : 'false';
  return {
    isWorkEquipmentDisplayed: equipmentSwitch,
    isMachineryTableDisplayed: equipmentSwitch,
    machineryTable,
    vehiclesTable,
  };
};

export const selectAnimalsValue = async (actionId: string) => {
  const {
    data: { action },
  } = await client.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: actionId,
    },
  });

  const animalsTable: { [key: string]: MachineryTypes } = {};

  if (action.animalUnitIDs.length > 0) {
    for (const animalUnitId of action.animalUnitIDs) {
      const {
        data: { animalUnit },
      } = await client.query({
        query: GET_ANIMAL_UNIT_BY_ID,
        variables: {
          id: animalUnitId,
        },
      });

      const {
        data: { animal },
      } = await client.query({
        query: GET_ANIMAL_BY_ID,
        variables: {
          id: animalUnit.animalIDs[0],
        },
      });

      const animalId = animal.id;

      animalsTable[animalId] = {
        ...animal,
        animalUnitId: animalUnit.id,
        ut: `${animalUnit.unit}`,
        statusId: animalUnit.animalStatusIDs[0] || '',
      };
    }
  }

  // ✅ FIX Task 10: Retornar 'true' solo si hay animales, 'false' si está vacío
  // Esto hace que el switch muestre "No hay" cuando no hay datos en ActionsValidation
  const hasAnimals = Object.keys(animalsTable).length > 0;
  return {
    isAnimalsTableDisplayed: hasAnimals ? 'true' : 'false',
    animalsTable,
  };
};
